import React, { useEffect, useRef, useState } from 'react';
import { RootContainer } from 'components/RootContainer';
import { MenuDrawer } from 'components/menuDrawer/MenuDrawer';
import { useIntl } from 'react-intl';
import msgId from 'resources/intl';
import { ShipManagement } from './ship/ShipManagement';
import {
  fetchAllList,
  setFetchError,
  useAdminSettingsAccounts,
  useAdminSettingsAccountGroups,
  useAdminSettingsShips,
  useAdminSettingsFetching,
  useAdminSettingsFetchError,
} from './adminSettingsSlice';
import { useDispatch } from 'react-redux';
import dimens from 'resources/dimens';
import { styled } from '@mui/material';
import { AccountGroupManagement } from './accountGroup/AccountGroupManagement';
import { AccountManagement } from './account/AccountManagement';
import { AccessAuthorityManagement } from './accessAuthority/AccessAuthorityManagement';
import { useAccount } from 'features/login/accountSlice';
import { ApiErrorDialog } from 'components/ApiErrorDialog';
import { FadeLoading } from 'components/FadeLoading';
import { DataRecalcManagement } from './dataRecalc/DataRecalcManagement';

const ContntsContainer = styled('div')({
  position: 'absolute',
  overflow: 'hidden',
  top: 0,
  height: '100%',
});

interface AdminSettingsFragmentProps {
  hidden: boolean;
}

export function AdminSettingsFragment(props: AdminSettingsFragmentProps): JSX.Element {
  const { hidden } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const account = useAccount();
  const accounts = useAdminSettingsAccounts();
  const accountGroups = useAdminSettingsAccountGroups();
  const ships = useAdminSettingsShips();
  const fetchError = useAdminSettingsFetchError();
  const fetching = useAdminSettingsFetching();
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(true);
  const [menuIndex, setMenuIndex] = useState(0);
  const menuTitles: string[] = [
    intl.formatMessage({ id: msgId.accountManagement }),
    intl.formatMessage({ id: msgId.groupManagement }),
    intl.formatMessage({ id: msgId.shipManagement }),
    intl.formatMessage({ id: msgId.authorityManagement }),
    intl.formatMessage({ id: msgId.dataRecalcRegistration }),
  ];
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  /**
   * メニュー選択変更
   * @param index 開閉状態
   */
  const handleSelectMenu = (index: number) => {
    setMenuIndex(index);
  };

  /**
   * メニュードロワー開閉状態変更
   * @param open 開閉状態
   */
  const handleMenuDrawerChangeOpen = (open: boolean) => {
    setMenuDrawerOpen(open);
  };

  /**
   * データ取得エラー
   */
  const handleFetchError = () => {
    dispatch(setFetchError(undefined));
  };

  useEffect(() => {
    if (account && account.authorityId <= 3) {
      if (accounts == null && accountGroups == null && ships == null) {
        dispatch(fetchAllList());
      }
    }
  }, [dispatch, account, accounts, accountGroups, ships]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      setContainerHeight(height);
    });
    containerRef.current && resizeObserver.observe(containerRef.current);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return (
    <RootContainer hidden={hidden} ref={containerRef}>
      <MenuDrawer
        menus={menuTitles}
        open={menuDrawerOpen}
        selectedMenu={menuIndex}
        onSelectMenu={handleSelectMenu}
        onChangeOpen={handleMenuDrawerChangeOpen}
      />
      <ContntsContainer
        sx={{
          width: menuDrawerOpen
            ? `calc(100% - ${dimens.subDrawer.width}px)`
            : `calc(100% - ${dimens.subDrawer.minWidth}px)`,
          left: menuDrawerOpen ? dimens.subDrawer.width : dimens.subDrawer.minWidth,
        }}
      >
        <AccountManagement open={!hidden && menuIndex === 0} height={containerHeight} />
        <AccountGroupManagement open={!hidden && menuIndex === 1} height={containerHeight} />
        <ShipManagement open={!hidden && menuIndex === 2} height={containerHeight} />
        <AccessAuthorityManagement open={!hidden && menuIndex === 3} />
        <DataRecalcManagement open={!hidden && menuIndex === 4} height={containerHeight} />
      </ContntsContainer>
      <FadeLoading loading={fetching} />
      {fetchError != null && <ApiErrorDialog error={fetchError} onClose={handleFetchError} />}
    </RootContainer>
  );
}
